import React from 'react';

import { useTranslation } from 'react-i18next';
import { FrameCard, OptionalSection, ColorPickerLabel, Section, Icon, Tooltip, ToggleContainer } from '@weezevent/nacre';

import { Input } from '../../inputs/inputs';
import { usePermissions } from '../../../../../user/permissions';
import Style from './widget-design.module.css';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import { AdminStatus } from '../../pages/new-widget';
require('codemirror/mode/javascript/javascript');
require('codemirror/mode/css/css');

function DimensionSelector({ width = 550, height = 450, onChange }) {
    const { t } = useTranslation();
    const [conserveRatio, setConserveRatio] = React.useState(false);

    const change = React.useCallback(
        ({ width: nWidth = null, height: nHeight = null }) => {
            if (!nWidth & !nHeight) {
                return;
            }

            if (conserveRatio) {
                const current_ratio = width / height;
                if (nWidth) {
                    nHeight = Math.round(nWidth * current_ratio);
                } else if (nHeight) {
                    nWidth = Math.round(nHeight / current_ratio);
                }

                return onChange({
                    width: nWidth,
                    height: nHeight
                });
            }

            // handle both changs (width & height)
            return onChange({
                width: nWidth || width,
                height: nHeight || height
            });
        },
        [width, height, conserveRatio, onChange]
    );

    return (
        <Section>
            <div className={Style['dimensions-container']}>
                <Input
                    label={t('ticket.widgets.event.config.design.width')}
                    value={width}
                    type="number"
                    onChange={({ target: { value } }) =>
                        change({
                            width: value,
                            height: null
                        })
                    }
                />
                <Tooltip triggerOnHover title={t(`${conserveRatio ? 'ticket.widgets.event.config.design.lock' : 'ticket.widgets.event.config.design.link'}`)}>
                    <Icon name={conserveRatio ? 'lock' : 'link'} onClick={() => setConserveRatio(r => !r)} />
                </Tooltip>

                <Input
                    label={t('ticket.widgets.event.config.design.height')}
                    value={height}
                    type="number"
                    onChange={({ target: { value } }) =>
                        change({
                            width: null,
                            height: value
                        })
                    }
                />
            </div>
        </Section>
    );
}

export default function WidgetDesign({ organization, onChange, form: { customs = {} }, allowedCapacities = ['PyvarCapacity.ADMIN'] }) {
    const { t } = useTranslation();
    const permissions = usePermissions();

    const change = React.useCallback(
        (name, value) => {
            onChange('customs', {
                ...customs,
                [name]: value
            });
        },
        [customs]
    );

    const { auto_dimension, height, width, mainColor, backgroundColor } = customs;

    const { css, ...values } = customs;

    return (
        <FrameCard id="design" title={t('ticket.widgets.common.module_design')} subtitle={t('ticket.widgets.common.module_design_subtitle')}>
            <div className={Style['color-container']}>
                <span style={{ marginRight: '50px' }}>
                    <ColorPickerLabel label={t('ticket.widgets.event.config.design.background_color')} onChange={(synth, color) => change('mainColor', color)} value={mainColor} />
                </span>
                <ColorPickerLabel
                    label={t('ticket.widgets.event.config.design.foreground_color')}
                    onChange={(synth, color) => change('backgroundColor', color)}
                    value={backgroundColor}
                />
            </div>
            <ToggleContainer
                toggle={auto_dimension}
                onToggle={checked => {
                    onChange('customs', {
                        ...customs,
                        auto_dimension: checked,
                        width,
                        height
                    });
                }}
                title={t('ticket.widgets.event.config.design.auto_dimension')}
                subtitle={t('ticket.widgets.event.config.design.auto_dimension_subtitle')}
            />

            {!auto_dimension && (
                <DimensionSelector
                    width={width}
                    height={height}
                    onChange={({ width, height }) => {
                        onChange('customs', {
                            ...customs,
                            width,
                            height
                        });
                    }}
                />
            )}

            {permissions.hasCapacities(allowedCapacities, organization.id) && (
                <OptionalSection
                    className={Style['optional-section__editor-css']}
                    label={
                        <span>
                            {t('ticket.widgets.event.config.advanced_options.title')}
                            <AdminStatus />
                        </span>
                    }
                >
                    <div>{t('ticket.widgets.event.config.css')}</div>
                    <CodeMirror
                        value={css}
                        options={{
                            mode: 'css',
                            lineNumbers: true
                        }}
                        onChange={(editor, data, value) => {
                            onChange('customs', {
                                ...customs,
                                css: value
                            });
                        }}
                    />
                    <div>{t('ticket.widgets.event.config.json')}</div>
                    <CodeMirror
                        value={JSON.stringify(values, null, 4)}
                        options={{
                            mode: 'javascript',
                            lineNumbers: true
                        }}
                        onChange={(editor, data, value) => {
                            try {
                                const json_value = JSON.parse(value);
                                onChange('customs', {
                                    ...customs,
                                    ...json_value
                                });
                            } catch (e) {
                                return e;
                            }
                        }}
                    />
                </OptionalSection>
            )}
        </FrameCard>
    );
}
